/* Expected backend points:
GET  /business_waiver_builder/smart_step5/:waiverId
PATCH /business_waiver_builder/smart_step5/:waiverId
*/

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import { useBusinessAuthContext } from "../commoncomponents/useBusinessAuthContext";
import API_URL from "../../api/API";
import { Lock, Pencil } from "lucide-react";
import { RichTextEditor } from "../../components/RichTextEditor";
import { replacePlaceholders, fixProseSpacing, normalizeHtmlEntities } from "../../api/helpers";
import WaiverStepLayout from "./WaiverStepLayout";
import WAIVER_STEP_RULES from "./WaiverStepConfig";

const BusinessSmartTemplateStep6 = () => {

  const { business } = useBusinessAuthContext();
  const navigate = useNavigate();
  const { waiverId } = useParams();

  const [sections, setSections] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  const [editorKey, setEditorKey] = useState(0);  //Rich Text Editor

  const [isDirty, setIsDirty] = useState(false); //Auto Save - to track changes
  const [saving, setSaving] = useState(false); //Prevent overlapping saves (VERY IMPORTANT)
  const [publishing, setPublishing] = useState(false); //Prevent overlapping clicks of Publish button

  const [waiver, setWaiver] = useState(null);  
  const stepRules = WAIVER_STEP_RULES["step6"] || {};
  const statusRule = stepRules[waiver?.waiverStatus] || {};
  const isReadOnly = statusRule.readOnly ?? false;
  const readOnlyReason = statusRule.reason ?? "";

  const [governingData, setGoverningData] = useState([]);

  const activeSection = sections[activeIndex];

  useEffect(() => {

    if (!business) {
      navigate("/business/login");
      return;
    }

    const fetchWaiver = async () => {

      try {

        const res = await fetch(
          `${API_URL.BUSINESS_WAIVER_BY_ID}/${waiverId}`,
          {
            headers: {
              Authorization: `Bearer ${business.token}`,
              "x-session-id": business.sessionId,
              "x-frontend-url": window.location.pathname
            }
          }
        );

        const data = await res.json();

        if (data.success) {

          const ordered = (data.templates.adultFull.sections || []).sort(
            (a, b) => a.order - b.order
          );

          setWaiver(data.waiver);   // ⭐ VERY IMPORTANT

          setSections(ordered);

          /* Governing Law must be second last, not last section */
          const governingSection = {
            _id: "governing-laws",
            title: "Governing Laws",
            status: "locked",
            system: true,
          };

          const lastSection = ordered[ordered.length - 1];

          setSections([
            ...ordered.slice(0, -1), // all except last
            governingSection,        // second last
            lastSection              // keep original last as last
          ]);

        }

      } catch (err) {
        console.error("Failed loading waiver sections", err);
      } finally {
        setLoading(false);
      }

    };

    fetchWaiver();

  }, [business, waiverId, navigate]);


  useEffect(() => {
    if (!waiver?.locations?.length) return;

    const loadGoverningLaws = async () => {
      try {
        // 1️⃣ get ALL business locations
        const locRes = await fetch(API_URL.BUSINESS_LOCATIONS, {
          headers: {
            Authorization: `Bearer ${business.token}`,
          },
        });

        const locJson = await locRes.json();

        // 2️⃣ keep only waiver locations
        /*const locations = locJson.locations.filter(loc =>
          waiver.locations.includes(loc._id)
        );*/

        const waiverLocationIds = waiver.locations.map(l =>
          String(typeof l === "string" ? l : l._id)
        );

        const locations = locJson.locations.filter(loc =>
          waiverLocationIds.includes(String(loc._id))
        );

        // 3️⃣ unique states
        const states = [...new Set(locations.map(l => l.state))];

        // 4️⃣ get clauses
        const clauseRes = await fetch(
          `${API_URL.GET_STATE_CLAUSES_FOR_WAIVER}/${waiverId}?states=${states.join(",")}`,
          {
            headers: {
              Authorization: `Bearer ${business.token}`,
              "x-session-id": business.sessionId,
              "x-frontend-url": window.location.pathname
            },
          }
        );

        const clauseJson = await clauseRes.json();

        // 5️⃣ build UI data per location
        const uiData = locations.map(loc => ({
          locationName: loc.name,
          state: loc.state,
          clause:
            clauseJson.data.find(
              c =>
                c.state.trim().toLowerCase() ===
                loc.state.trim().toLowerCase()
            )?.body ||
            "<p>No governing law found for this state.</p>",
        }));

        setGoverningData(uiData);
      } catch (err) {
        console.error("Failed loading governing laws", err);
      }
    };

    loadGoverningLaws();
  }, [waiver, waiverId, business.token]);

  
  /* Update Rich Text Editor when section changes */
  useEffect(() => {
    setEditorKey(prev => prev + 1);
  }, [activeIndex]);


  const updateContent = (value) => {

    if (activeSection.system) return;  // ⭐ IMPORTANT

    const updated = [...sections];
    updated[activeIndex].content = value;

    setSections(updated);
    setIsDirty(true); // ✅ mark as changed

  };

  // Add debounce auto-save effect
  useEffect(() => {
    if (!isDirty) return;

    const timer = setTimeout(() => {
      saveDraft();
      setIsDirty(false);
    }, 1500); // ⏱️ 1.5 sec after user stops typing

    return () => clearTimeout(timer); // cancel if user keeps typing
  }, [sections, isDirty]); // trigger when sections change. If you only depend on sections, then switching section (without edit) can trigger save unnecessarily

  //Users may switch sections quickly → prevent data loss:
  const handleSectionChange = (index) => {
    if (isDirty) {
      saveDraft(); // flush pending changes
      setIsDirty(false);
    }
    setActiveIndex(index);
  };

  const saveDraft = async () => {
    if (saving) return false; // prevent parallel calls

    if (publishing) return false; //Optional improvement inside saveDraft

    setSaving(true);

    try {
      const res = await fetch(
        `${API_URL.BUSINESS_WAIVER_SMART_STEP_WAIVER_SECTIONS}/${waiverId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${business.token}`,
            "x-session-id": business.sessionId,
            "x-frontend-url": window.location.pathname
          },
          body: JSON.stringify({ sections })
        }
      );

      const data = await res.json();

      if (!data.success) {
        console.error(data.message);
        alert(data.message); //sine auto save, user wont be intimitaded othweise if save fails
        return false; // ✅ important
      }

      return true;

    } catch (err) {
      console.error("Auto-save failed", err);
      return false; // ✅ important
    } finally {
      setSaving(false);
    }
  };


  const handlePublish = async () => {

    if (saving || publishing) return;

    setPublishing(true);

    try {

      const success = await saveDraft();

      if (!success) {
        alert("Failed to save changes before publishing");
        return;
      }

      const res = await fetch(
        `${API_URL.BUSINESS_WAIVER_COMPLETE_STEP_WAIVER_SECTIONS}/${waiverId}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${business.token}`,
            "x-session-id": business.sessionId,
            "x-frontend-url": window.location.pathname,
            "Content-Type": "application/json"
          }
        }
      );

      let data;

      try {
        data = await res.json();
      } catch {
        alert("Unexpected server response");
        return;
      }

      if (!data.success) {
        alert(data.message);
      } else {
        navigate(`/business/waiverbuilder/smart/${waiverId}/step-7`);
      }

    } finally {
      setPublishing(false);
    }
  };

 /*  const handlePublish = async () => {

    if (saving) return;
    

    const success = await saveDraft();

    if (!success) {
      alert("Failed to save changes before publishing");
      return;
    }

    const res = await fetch(
      `${API_URL.BUSINESS_WAIVER_COMPLETE_STEP_WAIVER_SECTIONS}/${waiverId}`,
      {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${business.token}`,
          "x-session-id": business.sessionId,
          "x-frontend-url": window.location.pathname,
          "Content-Type": "application/json"
        }
      }
    );

    let data;
    try {
      data = await res.json();
    } catch {
      alert("Unexpected server response");
      return;
    }


    if (!data.success) {
      alert(data.message);
    } else {
      navigate(`/business/waiverbuilder/smart/${waiverId}/step-7`);
    }
  };
*/

  if (loading) return <div>Loading...</div>;

  //Placeholder replacement logic
  const businessName = waiver?.businessId?.businessName || "";
  const businessEmail = waiver?.businessId?.email || "";
  const county =  waiver?.locations?.[0]?.county || "";

  const phValues = {
    BUSINESS_NAME: businessName,
    BUSINESS_EMAIL: businessEmail,
    COUNTY: county,
    MINOR_FULL_NAME: "the minor",
    MINOR_FIRST_NAME: "the minor",
    PREVIOUS_SIGNED_DATE: "THE PREVIOUS SIGNED DATE",
  };
 

 
  return (
    <>
    {/* 
    <div className="h-screen flex bg-background">

      <Sidebar />

      <div className="flex-1 overflow-auto py-10">*/ }
      <WaiverStepLayout  waiver={waiver} currentStep="step-6"  isReadOnly={isReadOnly} readOnlyReason={readOnlyReason} >
        

        <div className="max-w-6xl mx-auto px-6">

          {/* HEADER */}

          <div className="flex items-center justify-between mb-6">
                {/* Back */}
                <button
                  onClick={() =>
                    navigate(`/business/waiverbuilder/smart/${waiverId}/step-5`)
                  }
                  className="text-sm text-muted-foreground"
                >
                ← Previous Step
                </button>

                {/* Next (only when readOnly) */}
                {isReadOnly && (
                  <button
                    onClick={() =>
                      navigate(`/business/waiverbuilder/smart/${waiverId}/step-7`)
                    }
                    className="text-sm text-muted-foreground"
                  >
                    Next Step →
                  </button>
                )}
              


            </div>

          <div className="flex justify-between items-center mb-8">

            <div>
              <h1 className="text-2xl font-semibold">
                Waiver Builder
              </h1>

              <p className="text-sm text-muted-foreground">
                Smart Template
              </p>
            </div>
              

            {!isReadOnly && ( 
            <div className="flex gap-3">

              <button
                onClick={saveDraft}
                disabled={saving || publishing}
                className={`px-5 py-2 border rounded-full text-sm ${
                  saving || publishing
                    ? "cursor-not-allowed opacity-50"
                    : ""
                }`}
              >
                {saving ? "Saving..." : "Save Draft"}
              </button>

              <button
                onClick={handlePublish}
                disabled={saving || publishing}
                className={`px-5 py-2 rounded-full font-medium ${
                  saving || publishing
                    ? "bg-yellow-100 text-yellow-400 cursor-not-allowed"
                    : "bg-yellow-500 hover:bg-yellow-600 text-black"
                }`}
              >
                {publishing ? "Publishing..." : "Review & Publish"}
              </button>

            </div>
            )}
          </div>


          <div className="flex gap-6">

            {/* LEFT SECTION NAV */}

            <div className="w-72 bg-white rounded-xl shadow-sm p-4">

              <div className="font-semibold mb-4">
                Waiver Sections
              </div>

              {sections.map((section, index) => {

                const locked = section.status === "locked";

                return (

                  <div
                    key={section._id || index}
                    onClick={() => handleSectionChange(index)}
                    className={`flex items-center justify-between px-4 py-3 rounded-lg mb-2 cursor-pointer
                    ${
                      activeIndex === index
                        ? "bg-black text-white"
                        : "hover:bg-gray-100"
                    }`}
                  >

                    <span className="text-sm">
                      {normalizeHtmlEntities(section.title)}
                    </span>

                    {locked ? (
                      <Lock size={14} />
                    ) : (
                      <Pencil size={14} />
                    )}

                  </div>

                );

              })}

            </div>



            {/* RIGHT EDITOR */}

            <div className="flex-1 bg-white rounded-xl shadow-sm">

              { activeSection && (

                <>

                  <div className="border-b px-6 py-4">

                    <div className="font-semibold">
                      {normalizeHtmlEntities(activeSection.title)}
                    </div>

                    <div className="text-sm text-muted-foreground">
                      {activeSection.status === "locked"
                        ? "This section is locked and cannot be edited."
                        : "Edit the content of this section."}
                    </div>

                  </div>

                  <div className="p-6">

                    {activeSection.system ? (

                      <div className="space-y-8">
                        {governingData.map((item, idx) => (
                          <div key={idx} className="border p-6 rounded-lg">
                            <h3 className="text-xl font-semibold mb-3">
                              {item.locationName} ({item.state})
                            </h3>
                            <div
                              className="prose max-w-none"
                                dangerouslySetInnerHTML={{
                                __html: fixProseSpacing(
                                          normalizeHtmlEntities(
                                            replacePlaceholders(item.clause, phValues, true, false)
                                          )
                                        ),
                              }}
                            />
                          </div>
                        ))}
                      </div>

                    ) : activeSection.status === "locked" ? (

                      <div
                        className="prose max-w-none [&_ul]:list-disc [&_ul]:ml-6 [&_ol]:list-decimal [&_ol]:ml-6 [&_li]:mb-2 [&_p]:mb-4"
                        dangerouslySetInnerHTML={{
                          __html: fixProseSpacing(
                                    normalizeHtmlEntities
                                    (
                                      replacePlaceholders(activeSection.content, phValues, true, false)
                                    )
                                  )
                          }}
                      />

                    ) : (

                      <RichTextEditor
                        key={editorKey}
                        content={activeSection.content}   // ✅ RAW HTML ONLY
                        onChange={(value) => updateContent(value)}
                      />

                    )}

                  </div>

                  {!isReadOnly && ( 
                  <div className="border-t px-6 py-3 text-sm text-muted-foreground flex items-center gap-2">
                    <div className="w-3 h-3 border rounded-full"></div>
                    {saving ? "Saving..." : "Changes saved"}
                  </div>
                  )}

                </>

              )}

            </div>

          </div>

        </div>

      </WaiverStepLayout>  
      {  /* </div>
    </div> */ }
    </>
  );

};

export default BusinessSmartTemplateStep6;